import { useEffect, useState, useCallback, useMemo } from 'preact/hooks';
import type { State, Payor, Specialties } from '@services/filter-api/types';
import { FILTER_SYNC_EVENTS, FILTER_EVENT_EMITTER_TYPES } from '../constants/eventTypes';

// Global state store for filter selections and validation
const globalFilterSelections = {
  state: null as State | null,
  insurance: null as Payor | null,
  specialty: null as Specialties | null,
  typeOfCare: undefined as string | undefined,
  errors: {
    state: "",
    insurance: "",
  } as {
    state: string;
    insurance: string;
  },
  showValidationMessage: false,
};

// Track which fields have been explicitly synced
const syncedFields = {
  state: false,
  insurance: false,
  specialty: false,
  typeOfCare: false,
  errors: false,
  showValidationMessage: false,
};

type FilterEventType = 
  | typeof FILTER_EVENT_EMITTER_TYPES.STATE_SELECTED
  | typeof FILTER_EVENT_EMITTER_TYPES.INSURANCE_SELECTED
  | typeof FILTER_EVENT_EMITTER_TYPES.NEEDS_SELECTED
  | typeof FILTER_EVENT_EMITTER_TYPES.TYPE_OF_CARE_CHANGED
  | typeof FILTER_EVENT_EMITTER_TYPES.VALIDATION_UPDATED
  | 'validation-message-updated';

interface FilterEvent {
  type: FilterEventType;
  payload?: any;
}

// Event emitter for cross-component communication
class FilterEventEmitter {
  private listeners: Map<FilterEventType, Set<(event: FilterEvent) => void>> = new Map();

  subscribe(eventType: FilterEventType, callback: (event: FilterEvent) => void) {
    if (!this.listeners.has(eventType)) {
      this.listeners.set(eventType, new Set());
    }
    this.listeners.get(eventType)!.add(callback);

    // Return unsubscribe function
    return () => {
      const callbacks = this.listeners.get(eventType);
      if (callbacks) {
        callbacks.delete(callback);
        if (callbacks.size === 0) {
          this.listeners.delete(eventType);
        }
      }
    };
  }

  emit(event: FilterEvent) {
    const callbacks = this.listeners.get(event.type);
    if (callbacks) {
      callbacks.forEach(callback => callback(event));
    }
  }
}

const filterEventEmitter = new FilterEventEmitter();

// Helper function to emit both internal and DOM events
const emitEvents = (eventType: FilterEventType, payload: any, domEventType: string) => {
  filterEventEmitter.emit({ type: eventType, payload });
  document.dispatchEvent(new CustomEvent(domEventType, { detail: payload }));
};

export const useFilterSync = () => {
  const [selectedItems, setSelectedItems] = useState({
    state: globalFilterSelections.state,
    insurance: globalFilterSelections.insurance,
    specialty: globalFilterSelections.specialty,
  });
  const [typeOfCare, setTypeOfCare] = useState(globalFilterSelections.typeOfCare);
  const [errors, setErrors] = useState(globalFilterSelections.errors);
  const [showValidationMessage, setShowValidationMessage] = useState(globalFilterSelections.showValidationMessage);

  // Update global state and mark fields as synced
  const updateGlobalState = useCallback((updates: Partial<typeof globalFilterSelections>) => {
    Object.assign(globalFilterSelections, updates);
    
    // Mark fields as synced when they're updated
    Object.keys(updates).forEach(key => {
      if (key in syncedFields) {
        syncedFields[key as keyof typeof syncedFields] = true;
      }
    });
  }, []);

  const handleGetSelectedState = useCallback((item: any) => {
    const newState = item.selected ? item : null;
    setSelectedItems(prev => ({ ...prev, state: newState }));
    updateGlobalState({ state: newState });
    
    // Clear state validation error when state is selected
    if (newState) {
      const updatedErrors = { ...globalFilterSelections.errors, state: "" };
      setErrors(updatedErrors);
      updateGlobalState({ errors: updatedErrors });
    }
    
    emitEvents(FILTER_EVENT_EMITTER_TYPES.STATE_SELECTED, newState, FILTER_SYNC_EVENTS.STATE_SELECTED);
  }, [updateGlobalState]);

  const handleGetSelectedInsurance = useCallback((item: any) => {
    const newInsurance = item.selected ? item : null;
    setSelectedItems(prev => ({ ...prev, insurance: newInsurance }));
    updateGlobalState({ insurance: newInsurance });
    
    // Clear insurance validation error when insurance is selected
    if (newInsurance) {
      const updatedErrors = { ...globalFilterSelections.errors, insurance: "" };
      setErrors(updatedErrors);
      updateGlobalState({ errors: updatedErrors });
    }
    
    emitEvents(FILTER_EVENT_EMITTER_TYPES.INSURANCE_SELECTED, newInsurance, FILTER_SYNC_EVENTS.INSURANCE_SELECTED);
  }, [updateGlobalState]);

  const handleGetSelectedNeeds = useCallback((items: any[]) => {
    const safeItems = items || [];
    const newSpecialty = safeItems.length ? safeItems : null;
    setSelectedItems(prev => ({ ...prev, specialty: newSpecialty }));
    updateGlobalState({ specialty: newSpecialty });
    
    // Always emit DOM event to ensure sync (consistent with other handlers)
    emitEvents(FILTER_EVENT_EMITTER_TYPES.NEEDS_SELECTED, newSpecialty, FILTER_SYNC_EVENTS.NEEDS_SELECTED);
  }, [updateGlobalState]);

  const handleTypeOfCareChange = useCallback((newTypeOfCare: string | undefined) => {
    setTypeOfCare(newTypeOfCare);
    updateGlobalState({ typeOfCare: newTypeOfCare });
    emitEvents(FILTER_EVENT_EMITTER_TYPES.TYPE_OF_CARE_CHANGED, newTypeOfCare, FILTER_SYNC_EVENTS.TYPE_OF_CARE_CHANGED);
  }, [updateGlobalState]);

  const handleValidationUpdate = useCallback((newErrors: { state: string; insurance: string }) => {
    setErrors(newErrors);
    updateGlobalState({ errors: newErrors });
    emitEvents(FILTER_EVENT_EMITTER_TYPES.VALIDATION_UPDATED, newErrors, FILTER_SYNC_EVENTS.VALIDATION_UPDATED);
  }, [updateGlobalState]);

  const handleValidationMessageUpdate = useCallback((show: boolean) => {
    setShowValidationMessage(show);
    updateGlobalState({ showValidationMessage: show });
    filterEventEmitter.emit({ type: 'validation-message-updated', payload: show });
  }, [updateGlobalState]);

  const eventHandlers = useMemo(() => ({
    state: (event: FilterEvent) => setSelectedItems(prev => ({ ...prev, state: event.payload })),
    insurance: (event: FilterEvent) => setSelectedItems(prev => ({ ...prev, insurance: event.payload })),
    needs: (event: FilterEvent) => setSelectedItems(prev => ({ ...prev, specialty: event.payload })),
    typeOfCare: (event: FilterEvent) => setTypeOfCare(event.payload),
    validation: (event: FilterEvent) => setErrors(event.payload),
    validationMessage: (event: FilterEvent) => setShowValidationMessage(event.payload),
  }), []);

  // Listen for events from other instances
  useEffect(() => {
    const subscriptions = [
      filterEventEmitter.subscribe(FILTER_EVENT_EMITTER_TYPES.STATE_SELECTED, eventHandlers.state),
      filterEventEmitter.subscribe(FILTER_EVENT_EMITTER_TYPES.INSURANCE_SELECTED, eventHandlers.insurance),
      filterEventEmitter.subscribe(FILTER_EVENT_EMITTER_TYPES.NEEDS_SELECTED, eventHandlers.needs),
      filterEventEmitter.subscribe(FILTER_EVENT_EMITTER_TYPES.TYPE_OF_CARE_CHANGED, eventHandlers.typeOfCare),
      filterEventEmitter.subscribe(FILTER_EVENT_EMITTER_TYPES.VALIDATION_UPDATED, eventHandlers.validation),
      filterEventEmitter.subscribe('validation-message-updated', eventHandlers.validationMessage),
    ];

    return () => {
      subscriptions.forEach(unsubscribe => unsubscribe());
    };
  }, [eventHandlers]);

  const returnValue = useMemo(() => ({
    selectedItems,
    typeOfCare,
    errors,
    showValidationMessage,
    handleGetSelectedState,
    handleGetSelectedInsurance,
    handleGetSelectedNeeds,
    handleTypeOfCareChange,
    handleValidationUpdate,
    handleValidationMessageUpdate,
    syncedFields,
  }), [
    selectedItems,
    typeOfCare,
    errors,
    showValidationMessage,
    handleGetSelectedState,
    handleGetSelectedInsurance,
    handleGetSelectedNeeds,
    handleTypeOfCareChange,
    handleValidationUpdate,
    handleValidationMessageUpdate,
  ]);

  return returnValue;
};
